import {Component, Inject, OnInit} from '@angular/core';
import {MatDialog, MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Organization} from "../../../../../../model/Organization";
import {ProjectFolders} from "../../../../../../model/supporting/ProjectFolders";
import {Draft} from "../../../../../../model/Draft";
import {Project} from "../../../../../../model/Project";
import {ProjectService} from "../../../../../service-data/project.service";
import {LanguagePipe} from "../../../../../pipe/language.pipe";

/**
 * "Move Draft to A Different Project".
 *
 * Moves via `ProjectService.moveDraftToProject` rather than
 * `DraftService.reassignProject`. The latter targets
 * `PUT /draft/reassignproject/:id/:newProjectId`, whose handler reads
 * `req.params.mewProjectId` — a typo, so the id is always `undefined`,
 * `Number(undefined)` is `NaN`, the Project lookup misses and the
 * server returns `{status: 'ERROR', message: 'New Project not found'}`
 * inside an HTTP 200. Nothing threw, the dialog closed, and the Draft
 * never moved. Reported to the server side; this dialog uses the
 * endpoint that works today either way (the same one Organize Projects
 * uses).
 */
@Component({
  selector: 'app-reasign-draft',
  templateUrl: './reasign-draft.component.html',
  styleUrls: ['./reasign-draft.component.css']
})

export class ReasignDraftComponent implements OnInit {
  public breakpoint: number; // Breakpoint observer code
  public draft: Draft;
  public allProjects: Project[];
  public hasFolders: boolean;

  organization: Organization;
  folders: ProjectFolders;

  filterByFolder = false;
  selectedProject: any = null;
  selectedFolderNode: any = null;

  /** True while the move is in flight. */
  moving = false;

  constructor(public dialog: MatDialog, private projectService: ProjectService,
              private snackBar: MatSnackBar,
              @Inject(MAT_DIALOG_DATA) data, private dialogRef: MatDialogRef<ReasignDraftComponent>) {
    this.draft = data.draft;
    this.allProjects = data.projects;
    this.folders = data.folders;
    this.hasFolders = !!(data.folders && data.folders.getFlattened && data.folders.getFlattened().length > 0);
  }

  public ngOnInit(): void {
    this.breakpoint = window.innerWidth <= 600 ? 1 : 2; // Breakpoint observer code
  }

  // tslint:disable-next-line:no-any
  public onResize(event: any): void {
    this.breakpoint = event.target.innerWidth <= 600 ? 1 : 2;
  }

  handleSelection(name: string) {
    this.selectedFolderNode = name;
    this.selectedProject = null;
  }

  onToggleChange() {
    this.selectedProject = null; // Clear selection to prevent hidden-state errors
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  /** Id of the Project this Draft is in now, or 0 when it is an orphan. */
  get currentProjectId(): number {
    if ( this.draft && this.draft.project && this.draft.project.ID ) {
      return this.draft.project.ID;
    }
    const workId = this.draft && this.draft.work_ID;
    return workId && Number(workId) > 0 ? Number(workId) : 0;
  }

  onConfirm(element: any): void {
    if ( this.moving || !this.draft || !this.selectedProject ) {
      return;
    }
    this.moving = true;
    if ( element && element.classList ) { element.classList.add('spinner'); }

    this.projectService.moveDraftToProject(this.draft.ID, this.currentProjectId, this.selectedProject.ID).subscribe(
      response => {
        this.moving = false;
        if ( element && element.classList ) { element.classList.remove('spinner'); }
        // Refusals come back as `status: 'ERROR'` inside a 200, so they
        // are invisible unless the body is read.
        if ( response && (response as any).status === 'ERROR' ) {
          console.error('moveDraftToProject refused', response);
          this.snackBar.open(LanguagePipe.get('The Draft could not be moved.'),
            LanguagePipe.get('Dismiss', 'compressSpaces'), { duration: 6000 });
          return;
        }
        this.dialogRef.close(this.selectedProject);
      },
      err => {
        this.moving = false;
        if ( element && element.classList ) { element.classList.remove('spinner'); }
        console.error('moveDraftToProject failed', err);
        this.snackBar.open(LanguagePipe.get('The Draft could not be moved.'),
          LanguagePipe.get('Dismiss', 'compressSpaces'), { duration: 6000 });
      }
    );
  }

  get filteredProjects() {
    if (!this.filterByFolder || !this.selectedFolderNode) {
      return this.allProjects;
    }
    // Logic: Only show projects belonging to the selected FileNode
    return this.allProjects.filter(p => p.folder === this.selectedFolderNode.id);
  }
}
